import { useTheme } from "@emotion/react";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import React, { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuth } from "react-oidc-context";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import LanguagePicker from "./components/LanguagePicker";
import MeasurementPicker from "./components/MeasurementPicker";
import { API_URL, clearAuthentication } from "./utils/lib";

const pages = [
  {
    name: "Navbar.home",
    path: "/",
  },
  {
    name: "Navbar.manufacturers",
    path: "/manufacturers",
  },
  {
    name: "Navbar.products",
    path: "/products",
  },
  {
    name: "Navbar.colors",
    path: "/colors",
  },
  {
    name: "Navbar.materials",
    path: "/materials",
  },
  {
    name: "Navbar.models",
    path: "/models",
  },
  {
    name: "Navbar.data",
    path: "/data"
  },
  {
    name: "Navbar.categories",
    path: "/attributes",
  },
  {
    name: "Navbar.objects",
    path: "/objects"
  },
  {
    name: "Blacklist",
    path: "/blacklist"
  }
];

const adminPages = [1, 8, 9];
const payingUsersPages = [3, 4, 5, 6];

export default function Navbar({ children, setLoggedIn, loggedIn, setUserInfo, userInfo }) {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [drawerState, setDrawerState] = React.useState(false);

  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      const { platform_roles, user_id, showroom_id, is_showroom_owner, modules, email, administration } = auth.user.profile;
      const isAdmin = platform_roles === 'Administrator' || platform_roles === 'Verwalter';
      if (!isAdmin && administration === 1 && !is_showroom_owner) {
        // user is not allowed to sign in to FILESAPI-frontend
        auth.signoutRedirect();
        return;
      }
      setLoggedIn(true);
      setUserInfo({ isAdmin, userId: user_id, ownerId: showroom_id, isPaying: modules != null ? modules.includes('my_catalog') : false, username: email })
      const tokenExpiryDate = new Date(auth.user.expires_at * 1000);
      Cookies.set('kcAccessToken', auth.user.access_token, {
        secure: true,
        sameSite: 'None',
        expires: tokenExpiryDate,
        path: '/imsFilesApi'
      });
    }
  }, [auth]);

  const theme = useTheme();
  const intl = useIntl();

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const navigate = useNavigate();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setDrawerState(open);
  };

  useEffect(() => {
    if (auth.isAuthenticated) return;
    axios
      .post(API_URL + '/checkAccessToken', undefined, { withCredentials: true })
      .then(response => {
        setLoggedIn(true);
        setUserInfo({ isAdmin: response.data.isAdmin, userId: response.data.userId, ownerId: response.data.ownerId, isPaying: response.data.isPaying, username: response.data.username });
      })
      .catch(error => {
        console.log(error);
        if (error.response.status === 403) {
          navigate('/login');
          setLoggedIn(false);
        }
      });
  }, []);

  const authenticationButton = useMemo(() => {
    if (loggedIn) {
      if (auth.isAuthenticated) {
        return <Button sx={{ marginLeft: 'auto' }} onClick={() => {
          setUserInfo(null);
          clearAuthentication();
          setLoggedIn(false);
          auth.signoutRedirect();
        }} color="inherit">Logout</Button>;
      } else {
        return <Button sx={{ marginLeft: 'auto' }} component={Link} to={"/"} onClick={() => {
          setUserInfo(null);
          axios
            .post(API_URL + '/logout', undefined, { withCredentials: true })
            .then(response => {
            })
            .catch(error => {
              console.log(error);
            })
            .finally(() => {
              clearAuthentication();
              setLoggedIn(false);
            })
        }} color="inherit">Logout</Button>
      }
    } else {
      return <Button sx={{ marginLeft: 'auto' }} component={Link} to={"/login"} color="inherit"><FormattedMessage id="Navbar.login" /></Button>;
    }
  }, [loggedIn, auth]);

  const drawerList = () => (
    <Box
      sx={{ width: 'auto', height: '100%', backgroundColor: theme.palette.primary.main }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {pages.map((page, idx) => {
          if ((!userInfo?.isAdmin && adminPages.includes(idx)) || (!userInfo?.isPaying && payingUsersPages.includes(idx))) {
            return;
          }
          return (
            <ListItem key={idx} disablePadding>
              <ListItemButton onClick={() => navigate(page.path)}>
                <ListItemText
                  primary={intl.formatMessage({ id: page.name })}
                  primaryTypographyProps={{ style: { color: '#ffffff' } }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2, display: { xs: 'relative', md: 'none' } }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
            {pages.map((page, idx) => {
              if ((!userInfo?.isAdmin && adminPages.includes(idx)) || (!userInfo?.isPaying && payingUsersPages.includes(idx))) {
                return;
              }
              return (
                <Button
                  component={NavLink}
                  to={page.path}
                  key={idx}
                  onClick={handleCloseNavMenu}
                  sx={{ my: 2, color: "white", display: "block" }}
                >
                  <FormattedMessage id={page.name} />
                </Button>
              );
            })}
          </Box>
          <MeasurementPicker />
          <LanguagePicker />
          {authenticationButton}
        </Toolbar>
        <Drawer
          open={drawerState}
          onClose={toggleDrawer(false)}
          anchor="left"
        >
          {drawerList()}
        </Drawer>
      </AppBar>
      <Outlet />
    </>
  );
}
